import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ─────────────────────────────────────────────────────────────────────────────
   Flapinaction.js — Interactive 3D Globe with LIVE tap events via SSE
   Fixes vs previous:
   1. API_BASE no longer strips /api — we always call /api/analytics/...
      so the path is correct without double /api/api/
   2. recent-taps endpoint path fixed (was /api/analytics/recent-taps,
      but API_BASE already included /api, so it doubled)
   3. Globe pre-populates from real events on mount
   4. SSE receives real profile_view / accessory_tap from UserInfo.jsx
      AND from device/tap NFC hardware scans
   5. Simulation only runs when no real events arrive within 6 seconds
───────────────────────────────────────────────────────────────────────────── */

// FIX: API_BASE already ends with /api — don't strip it.
// All fetch calls use this directly: `${API_BASE}/analytics/recent-taps`
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '');

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

.fia-section {
  padding: 100px 5%;
  background: #f0f5ff;
  font-family: 'DM Sans', sans-serif;
}
.fia-inner { max-width: 1100px; margin: 0 auto; }
.fia-label {
  display: block; font-size: 0.72rem; font-weight: 700;
  letter-spacing: 2.5px; text-transform: uppercase;
  color: #1c73ba; margin-bottom: 12px;
}
.fia-heading {
  font-size: clamp(1.8rem, 3vw + 0.5rem, 2.8rem);
  font-weight: 700; color: #06112b; line-height: 1.15; margin-bottom: 8px;
}
.fia-heading em { font-style: normal; color: #1c73ba; }
.fia-sub { font-size: 0.95rem; color: #64748b; margin-bottom: 32px; max-width: 500px; }

.fia-status-row {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 12px; margin-bottom: 24px;
}
.fia-badge {
  display: inline-flex; align-items: center; gap: 8px;
  background: #fff; border: 1px solid rgba(28,115,186,0.15);
  border-radius: 99px; padding: 6px 16px;
  font-size: 0.8rem; font-weight: 600; color: #1e293b;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.fia-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.fia-dot.live { background: #10b981; animation: fiaPulse 2s infinite; }
.fia-dot.sim  { background: #94a3b8; }
.fia-badge strong { color: #1c73ba; }
@keyframes fiaPulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.4); }
  50%      { box-shadow: 0 0 0 5px rgba(16,185,129,0); }
}
.fia-legend { display: flex; gap: 16px; flex-wrap: wrap; }
.fia-legend-item { display: flex; align-items: center; gap: 6px; font-size: 0.75rem; color: #64748b; }
.fia-legend-dot  { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

.fia-globe-wrap {
  position: relative;
  border-radius: 20px;
  overflow: visible;
  background: linear-gradient(145deg, #0a1628 0%, #0d1f45 60%, #06112b 100%);
  border: 1px solid rgba(28,115,186,0.2);
  box-shadow: 0 12px 60px rgba(6,17,43,0.25);
  display: flex; align-items: center; justify-content: center;
  min-height: 480px;
}
.fia-globe-canvas { display: block; border-radius: 20px; cursor: grab; }
.fia-globe-canvas:active { cursor: grabbing; }
.fia-starfield {
  position: absolute; inset: 0; border-radius: 20px;
  overflow: hidden; pointer-events: none; z-index: 0;
}
.fia-globe-overlay {
  position: absolute; inset: 0;
  pointer-events: none; z-index: 10; overflow: visible;
}

.fia-popup {
  position: absolute; pointer-events: none;
  transform: translate(-50%, -100%) translateY(-14px);
  transition: opacity 0.35s ease, transform 0.35s ease;
  z-index: 20; filter: drop-shadow(0 8px 24px rgba(0,0,0,0.4));
}
.fia-popup.entering { opacity: 0; transform: translate(-50%, -100%) translateY(-4px); }
.fia-popup.visible  { opacity: 1; transform: translate(-50%, -100%) translateY(-14px); }
.fia-popup.leaving  { opacity: 0; transform: translate(-50%, -100%) translateY(-20px); }
.fia-popup-box {
  background: rgba(6,17,43,0.95); border-radius: 12px; padding: 10px 14px;
  min-width: 160px; max-width: 210px; backdrop-filter: blur(20px); white-space: nowrap;
}
.fia-popup-top { display: flex; align-items: center; gap: 9px; margin-bottom: 5px; }
.fia-popup-initials {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: #fff; flex-shrink: 0;
}
.fia-popup-avatar {
  width: 26px; height: 26px; border-radius: 50%;
  object-fit: cover; flex-shrink: 0;
}
.fia-popup-name { font-size: 12px; font-weight: 700; color: #e8f4fd; overflow: hidden; text-overflow: ellipsis; flex: 1; }
.fia-popup-action { font-size: 10px; margin-top: 1px; font-weight: 500; }
.fia-popup-device { font-size: 12px; flex-shrink: 0; margin-left: auto; }
.fia-popup-loc { display: flex; align-items: center; gap: 4px; font-size: 10px; color: rgba(148,163,184,0.8); }
.fia-popup-biz { font-size: 9px; color: rgba(28,115,186,0.8); margin-top: 2px; }
.fia-popup-arrow {
  position: absolute; bottom: -7px; left: 50%; transform: translateX(-50%);
  width: 0; height: 0;
  border-left: 7px solid transparent; border-right: 7px solid transparent;
}
.fia-hint {
  position: absolute; bottom: 16px; right: 20px;
  font-size: 0.72rem; color: rgba(255,255,255,0.3);
  pointer-events: none; user-select: none;
}

.fia-ticker {
  margin-top: 20px; overflow: hidden; height: 36px;
  border-radius: 10px; background: rgba(28,115,186,0.07);
  border: 1px solid rgba(28,115,186,0.12);
  display: flex; align-items: center; padding: 0 16px; gap: 12px;
}
.fia-ticker-label { font-size: 0.7rem; font-weight: 700; color: #1c73ba; white-space: nowrap; text-transform: uppercase; letter-spacing: 1px; }
.fia-ticker-items { display: flex; gap: 24px; animation: tickerScroll 30s linear infinite; }
.fia-ticker-item { font-size: 0.78rem; color: #334155; white-space: nowrap; }
.fia-ticker-item span { color: #1c73ba; font-weight: 600; }
@keyframes tickerScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
`;

const TYPE_CFG = {
  profile_view:     { label:'viewed a profile',   color:'#1c73ba', ring:'#5aaef7', hex:0x1c73ba },
  accessory_tap:    { label:'tapped a card',       color:'#059669', ring:'#34d399', hex:0x059669 },
  link_click:       { label:'clicked a link',      color:'#7c3aed', ring:'#a78bfa', hex:0x7c3aed },
  contact_save:     { label:'saved contact',       color:'#d97706', ring:'#fbbf24', hex:0xd97706 },
  contact_exchange: { label:'exchanged contact',   color:'#db2777', ring:'#f472b6', hex:0xdb2777 },
  checkin:          { label:'checked in',          color:'#0891b2', ring:'#22d3ee', hex:0x0891b2 },
};
const DEFAULT_CFG = { label:'was active', color:'#1c73ba', ring:'#5aaef7', hex:0x1c73ba };

const SIM_NAMES  = ['Alex','Priya','Carlos','Yuki','Fatima','Liam','Aisha','Noah','Chen','Amara'];
const SIM_CITIES = ['Kathmandu','Mumbai','New York','Tokyo','London','Sydney','Toronto','Dubai','Seoul','Lagos'];
const SIM_TYPES  = ['profile_view','accessory_tap','link_click','contact_save'];
const DEVICE_ICONS = { mobile:'📱', desktop:'💻', tablet:'📲', unknown:'🌐' };

const HOTSPOTS = [
  {lat:51,   lon:-0.1,  w:8},{lat:40.7,lon:-74,   w:8},{lat:28.6,lon:77,   w:9},
  {lat:35.7, lon:139,   w:7},{lat:-23.5,lon:-46.6,w:6},{lat:39.9,lon:116,  w:7},
  {lat:48.9, lon:2.3,   w:6},{lat:52.5,lon:13.4,  w:5},{lat:19.1,lon:72.9, w:7},
  {lat:1.3,  lon:103.8, w:5},{lat:27.7,lon:85.3,  w:6},{lat:43.7,lon:-79.4,w:5},
];
function randomHotspot() {
  const tw = HOTSPOTS.reduce((s,h)=>s+h.w,0);
  let r = Math.random()*tw;
  for (const h of HOTSPOTS) { r-=h.w; if(r<=0) return {lat:h.lat+(Math.random()-.5)*6,lon:h.lon+(Math.random()-.5)*6}; }
  return HOTSPOTS[0];
}

function latLonToVec3(lat, lon, radius) {
  const phi   = (90 - lat)  * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return {
    x:  radius * Math.sin(phi) * Math.cos(theta),
    y:  radius * Math.cos(phi),
    z: -radius * Math.sin(phi) * Math.sin(theta),
  };
}

function project3D(vec3, camera, renderer, THREE) {
  if (!THREE) return null;
  const v = new THREE.Vector3(vec3.x, vec3.y, vec3.z);
  v.project(camera);
  const canvas = renderer.domElement;
  const x = ( v.x * 0.5 + 0.5) * canvas.clientWidth;
  const y = (-v.y * 0.5 + 0.5) * canvas.clientHeight;
  if (v.z > 1) return null;
  return { x, y };
}

// ── Popup ─────────────────────────────────────────────────────────────────────
function PopupCard({ ev, screenPos, onExpire }) {
  const [phase, setPhase] = useState('entering');
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('visible'),  30);
    const t2 = setTimeout(() => setPhase('leaving'), 3800);
    const t3 = setTimeout(() => onExpire(ev.id),     4300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []); // eslint-disable-line
  if (!screenPos) return null;
  const cfg = TYPE_CFG[ev.type] || DEFAULT_CFG;
  const loc = [ev.city, ev.country].filter(Boolean).join(', ') || 'Nepal';
  return (
    <div className={`fia-popup ${phase}`} style={{ left: screenPos.x, top: screenPos.y }}>
      <div className="fia-popup-box" style={{ border:`1px solid ${cfg.ring}44` }}>
        <div className="fia-popup-top">
          {/* Show real user photo if available, otherwise initials */}
          {ev.photo
            ? <img src={ev.photo} alt={ev.name} className="fia-popup-avatar"
                onError={e => { e.target.style.display='none'; }}/>
            : <div className="fia-popup-initials"
                style={{ background:`linear-gradient(135deg,${cfg.color},${cfg.ring})` }}>
                {(ev.name||'?')[0].toUpperCase()}
              </div>
          }
          <div style={{ minWidth:0, flex:1 }}>
            <div className="fia-popup-name">{ev.name}</div>
            <div className="fia-popup-action" style={{ color:cfg.ring }}>{cfg.label}</div>
          </div>
          <span className="fia-popup-device">{DEVICE_ICONS[ev.device]||'🌐'}</span>
        </div>
        <div className="fia-popup-loc"><span>📍</span><span>{loc}</span></div>
        {ev.business && <div className="fia-popup-biz">🏢 {ev.business}</div>}
        {ev.tagUid   && <div className="fia-popup-biz">🪪 {ev.tagUid}</div>}
      </div>
      <div className="fia-popup-arrow" style={{ borderTop:`8px solid rgba(6,17,43,0.95)` }}/>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function Flapinaction() {
  const canvasRef    = useRef(null);
  const wrapRef      = useRef(null);
  const threeRef     = useRef({});
  const markersRef   = useRef([]);
  const animFrameRef = useRef(null);
  const simRef       = useRef(null);
  const liveTimer    = useRef(null);
  const isLiveRef    = useRef(false); // ref copy so setTimeout closures see latest

  const [popups,          setPopups]          = useState([]);
  const [screenPositions, setScreenPositions] = useState({});
  const [totalCount,      setTotalCount]      = useState(0);
  const [isLive,          setIsLive]          = useState(false);
  const [threeReady,      setThreeReady]      = useState(false);
  const [ticker,          setTicker]          = useState([]);

  // keep ref in sync
  useEffect(() => { isLiveRef.current = isLive; }, [isLive]);

  // ── Load Three.js ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.THREE) { initGlobe(); return; }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
    script.onload = () => initGlobe();
    document.head.appendChild(script);
    return () => { script.onload = null; };
  }, []); // eslint-disable-line

  function initGlobe() {
    if (!canvasRef.current) return;
    const THREE = window.THREE;
    threeRef.current.THREE = THREE;

    const canvas = canvasRef.current;
    const wrap   = wrapRef.current;
    const W = wrap.clientWidth;
    const H = Math.max(460, Math.round(W * 0.55));
    canvas.width = W; canvas.height = H;

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setSize(W, H);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, W / H, 0.1, 1000);
    camera.position.set(0, 0, 2.8);

    scene.add(new THREE.AmbientLight(0xffffff, 0.35));
    const sun = new THREE.DirectionalLight(0x8ab4f8, 1.4);
    sun.position.set(5, 3, 5); scene.add(sun);
    const rim = new THREE.DirectionalLight(0x1c73ba, 0.5);
    rim.position.set(-4, -2, -4); scene.add(rim);

    const texSize = 2048;
    const texCanvas = document.createElement('canvas');
    texCanvas.width = texSize; texCanvas.height = texSize / 2;
    const ctx = texCanvas.getContext('2d');
    const ocean = ctx.createLinearGradient(0, 0, 0, texSize/2);
    ocean.addColorStop(0, '#0d2a52'); ocean.addColorStop(0.5, '#0f3460'); ocean.addColorStop(1, '#0a1f3d');
    ctx.fillStyle = ocean; ctx.fillRect(0, 0, texSize, texSize/2);

    const texture = new THREE.CanvasTexture(texCanvas);
    const globe = new THREE.Mesh(
      new THREE.SphereGeometry(1, 64, 64),
      new THREE.MeshPhongMaterial({ map: texture, specular: new THREE.Color(0x1c73ba), shininess: 12 })
    );
    scene.add(globe);

    scene.add(new THREE.Mesh(
      new THREE.SphereGeometry(1.06, 64, 64),
      new THREE.MeshPhongMaterial({ color: 0x3b9eff, transparent: true, opacity: 0.06 })
    ));

    drawLandTexture(ctx, texSize, () => { texture.needsUpdate = true; });
    addGraticule(scene, THREE);

    Object.assign(threeRef.current, { THREE, scene, camera, renderer, globe });
    setThreeReady(true);

    const state = { autoRotate: true, isDragging: false, prevMouse: {x:0,y:0}, rotY: 0, rotX: 0, velX: 0 };
    threeRef.current.state = state;

    const onDown = (mx, my) => {
      state.isDragging = true; state.autoRotate = false;
      state.prevMouse = { x: mx, y: my };
    };
    const onMove = (mx, my) => {
      if (!state.isDragging) return;
      const dx = mx - state.prevMouse.x, dy = my - state.prevMouse.y;
      state.velX = dx * 0.008;
      state.rotY += dx * 0.008;
      state.rotX = Math.max(-1.1, Math.min(1.1, state.rotX + dy * 0.006));
      state.prevMouse = { x: mx, y: my };
    };
    const onUp = () => { state.isDragging = false; setTimeout(() => { state.autoRotate = true; }, 2000); };

    canvas.addEventListener('mousedown',  e => onDown(e.clientX, e.clientY));
    canvas.addEventListener('mousemove',  e => onMove(e.clientX, e.clientY));
    canvas.addEventListener('touchstart', e => onDown(e.touches[0].clientX, e.touches[0].clientY), { passive: true });
    canvas.addEventListener('touchmove',  e => { e.preventDefault(); onMove(e.touches[0].clientX, e.touches[0].clientY); }, { passive: false });
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      if (state.autoRotate) { state.rotY += 0.003; }
      else if (!state.isDragging) { state.velX *= 0.92; state.rotY += state.velX; }
      globe.rotation.y = state.rotY;
      globe.rotation.x = state.rotX;
      renderer.render(scene, camera);
      const newPos = {};
      markersRef.current.forEach(m => {
        const v = latLonToVec3(m.lat, m.lon, 1);
        const vec = new THREE.Vector3(v.x, v.y, v.z);
        vec.applyEuler(new THREE.Euler(globe.rotation.x, globe.rotation.y, 0, 'XYZ'));
        const proj = project3D({ x: vec.x, y: vec.y, z: vec.z }, camera, renderer, THREE);
        newPos[m.id] = proj ? { x: proj.x, y: proj.y } : null;
        if (m.mesh) { m.mesh.position.set(vec.x*1.02, vec.y*1.02, vec.z*1.02); m.mesh.visible = vec.z > -0.15; }
      });
      setScreenPositions({ ...newPos });
    };
    animate();

    const ro = new ResizeObserver(() => {
      const nW = wrap.clientWidth, nH = Math.max(460, Math.round(nW * 0.55));
      renderer.setSize(nW, nH); camera.aspect = nW / nH; camera.updateProjectionMatrix();
    });
    ro.observe(wrap);
    threeRef.current.ro = ro;
    threeRef.current.cleanup = () => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
      ro.disconnect();
    };
  }

  function drawLandTexture(ctx, texSize, onDone) {
    const W = texSize, H = texSize / 2;
    const px = lon => ((lon + 180) / 360) * W;
    const py = lat => ((90 - lat)  / 180) * H;
    ctx.fillStyle = '#1a4a7a';
    const LAND = [
      [[-168,71],[-140,70],[-96,74],[-84,73],[-64,63],[-52,47],[-66,44],[-70,42],[-80,25],[-88,15],[-77,8],[-90,14],[-105,19],[-117,32],[-118,34],[-122,37],[-124,47],[-130,54],[-138,60],[-148,60],[-163,60],[-168,71]],
      [[-73,76],[-18,76],[-17,70],[-22,64],[-45,60],[-52,65],[-55,72],[-73,76]],
      [[-80,10],[-62,11],[-50,3],[-35,-5],[-35,-10],[-40,-20],[-52,-33],[-65,-55],[-68,-50],[-72,-45],[-73,-38],[-72,-28],[-68,-22],[-62,-13],[-55,-2],[-50,1],[-60,7],[-80,10]],
      [[28,72],[10,63],[-2,57],[-8,52],[0,48],[3,44],[10,44],[15,38],[28,36],[26,40],[30,47],[24,47],[22,49],[16,50],[14,54],[10,55],[6,53],[2,51],[6,53],[8,57],[5,62],[10,63],[15,66],[28,72]],
      [[-5,35],[12,37],[32,31],[37,22],[43,12],[44,2],[40,-3],[36,-17],[33,-26],[26,-33],[18,-34],[12,-28],[10,-18],[9,-5],[9,5],[4,5],[-5,5],[-14,10],[-16,20],[-12,28],[-5,35]],
      [[28,72],[60,75],[100,72],[140,72],[160,68],[168,64],[160,56],[132,43],[130,35],[120,25],[110,20],[100,5],[98,2],[100,-2],[108,-8],[115,-8],[108,20],[100,22],[90,24],[78,8],[74,8],[66,22],[56,26],[44,12],[36,12],[32,31],[28,40],[24,47],[28,55],[38,56],[40,60],[44,68],[56,72],[80,73],[100,72]],
      [[114,-22],[122,-18],[132,-12],[138,-12],[142,-18],[148,-20],[152,-24],[150,-36],[142,-38],[130,-33],[120,-34],[114,-30],[114,-22]],
    ];
    LAND.forEach(poly => {
      ctx.beginPath(); ctx.moveTo(px(poly[0][0]), py(poly[0][1]));
      poly.slice(1).forEach(([lon,lat]) => ctx.lineTo(px(lon), py(lat)));
      ctx.closePath(); ctx.fill();
    });
    ctx.strokeStyle = 'rgba(28,115,186,0.15)'; ctx.lineWidth = 0.5;
    for (let lat=-90;lat<=90;lat+=30) { ctx.beginPath(); ctx.moveTo(0,py(lat)); ctx.lineTo(W,py(lat)); ctx.stroke(); }
    for (let lon=-180;lon<=180;lon+=30) { ctx.beginPath(); ctx.moveTo(px(lon),0); ctx.lineTo(px(lon),H); ctx.stroke(); }
    onDone();
  }

  function addGraticule(scene, THREE) {
    const mat = new THREE.LineBasicMaterial({ color: 0x1c73ba, transparent: true, opacity: 0.08 });
    const R = 1.001;
    for (let i=0;i<6;i++) {
      const lat = (i/6)*180-90; const phi = (90-lat)*Math.PI/180;
      const pts = Array.from({length:65},(_,j)=>{ const a=(j/64)*Math.PI*2; return new THREE.Vector3(R*Math.sin(phi)*Math.cos(a),R*Math.cos(phi),-R*Math.sin(phi)*Math.sin(a)); });
      scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat));
    }
    for (let i=0;i<6;i++) {
      const angle=(i/6)*Math.PI*2;
      const pts = Array.from({length:65},(_,j)=>{ const a=(j/64)*Math.PI*2; return new THREE.Vector3(R*Math.sin(a)*Math.cos(angle),R*Math.cos(a),-R*Math.sin(a)*Math.sin(angle)); });
      scene.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), mat));
    }
  }

  function addDotToGlobe(lat, lon, color) {
    const { THREE, scene } = threeRef.current;
    if (!THREE || !scene) return null;
    const dot = new THREE.Mesh(new THREE.SphereGeometry(0.022, 10, 10), new THREE.MeshBasicMaterial({ color }));
    const v = latLonToVec3(lat, lon, 1.02);
    dot.position.set(v.x, v.y, v.z);
    scene.add(dot);
    return dot;
  }

  // ── Core: add a marker + popup ─────────────────────────────────────────────
  const addMarker = useCallback((ev) => {
    const hs  = (!ev.lat || !ev.lon) ? randomHotspot() : { lat: ev.lat, lon: ev.lon };
    const lat = hs.lat, lon = hs.lon;
    const cfg = TYPE_CFG[ev.type] || DEFAULT_CFG;
    const id  = ev.id ? String(ev.id) : String(Math.random());

    const mesh = threeRef.current.THREE ? addDotToGlobe(lat, lon, cfg.hex) : null;
    markersRef.current = [...markersRef.current.slice(-14), { id, lat, lon, cfg, mesh }];

    const markerEv = { ...ev, id, lat, lon };
    setPopups(p => [...p.slice(-11), markerEv]);
    setTotalCount(n => n + 1);

    setTicker(t => {
      const loc = [ev.city, ev.country].filter(Boolean).join(', ') || 'Nepal';
      const item = `${ev.name || 'Someone'} ${cfg.label} in ${loc}`;
      return [...t.slice(-19), item];
    });

    setTimeout(() => {
      markersRef.current = markersRef.current.filter(m => m.id !== id);
      if (mesh && threeRef.current.scene) threeRef.current.scene.remove(mesh);
      setPopups(p => p.filter(e => e.id !== id));
    }, 4500);
  }, []); // eslint-disable-line

  const removePopup = useCallback((id) => setPopups(p => p.filter(e => e.id !== id)), []);

  // ── Simulation fallback ────────────────────────────────────────────────────
  const startSim = useCallback(() => {
    if (simRef.current) return;
    simRef.current = setInterval(() => {
      const hs = randomHotspot();
      addMarker({
        id: String(Math.random()),
        type: SIM_TYPES[Math.floor(Math.random() * SIM_TYPES.length)],
        name: SIM_NAMES[Math.floor(Math.random() * SIM_NAMES.length)],
        city: SIM_CITIES[Math.floor(Math.random() * SIM_CITIES.length)],
        country: null, device: ['mobile','desktop','tablet'][Math.floor(Math.random()*3)],
        lat: hs.lat, lon: hs.lon, photo: null,
      });
    }, 1800 + Math.random() * 700);
  }, [addMarker]);

  const stopSim = useCallback(() => {
    if (simRef.current) { clearInterval(simRef.current); simRef.current = null; }
  }, []);

  // ── Pre-populate from recent-taps REST endpoint ────────────────────────────
  // FIX: API_BASE already = "http://host/api" so path is /analytics/recent-taps
  useEffect(() => {
    if (!threeReady) return;
    fetch(`${API_BASE}/analytics/recent-taps?limit=10`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.ok && data.events?.length) {
          data.events.slice(0, 6).forEach((ev, i) => {
            setTimeout(() => addMarker(ev), i * 500);
          });
          setTotalCount(data.events.length);
        }
      })
      .catch(() => {}); // silent — simulation covers this
  }, [threeReady, addMarker]);

  // ── SSE live stream ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!threeReady) return;
    let sse;

    const connect = () => {
      try {
        // FIX: same API_BASE fix — /analytics/live-stream not /api/analytics/live-stream
        sse = new EventSource(`${API_BASE}/analytics/live-stream`);

        sse.onopen = () => {
          // Give it 6 seconds to receive a real event before starting simulation
          liveTimer.current = setTimeout(() => {
            if (!isLiveRef.current) startSim();
          }, 60000);
        };

        sse.onmessage = (e) => {
          try {
            const ev = JSON.parse(e.data);
            // Heartbeat comments ": heartbeat" don't trigger onmessage — only real data does
            setIsLive(true);
            stopSim();
            clearTimeout(liveTimer.current);
            // If no new live event in 15s, fall back to simulation
            liveTimer.current = setTimeout(() => {
              setIsLive(false);
              startSim();
            }, 15_000);
            addMarker(ev);
          } catch (_) {}
        };

        sse.onerror = () => {
          console.warn('[SSE] connection lost — using simulation');
          sse.close();
          setIsLive(false);
          startSim();
        };
      } catch {
        startSim();
      }
    };

    connect();

    return () => {
      clearTimeout(liveTimer.current);
      if (sse) sse.close();
    };
  }, [threeReady]); // eslint-disable-line

  useEffect(() => {
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      stopSim();
      if (threeRef.current.cleanup) threeRef.current.cleanup();
      if (threeRef.current.renderer) threeRef.current.renderer.dispose();
    };
  }, []); // eslint-disable-line

  const tickerItems = ticker.length
    ? [...ticker, ...ticker]
    : SIM_CITIES.map((c, i) => `${SIM_NAMES[i % SIM_NAMES.length]} tapped a card in ${c}`).flatMap(x=>[x,x]);

  return (
    <section className="fia-section">
      <style>{STYLES}</style>
      <div className="fia-inner">
        <span className="fia-label">Live Activity</span>
        <h2 className="fia-heading">Flap in <em>Action</em></h2>
        <p className="fia-sub">Every tap, every view — happening around the world right now.</p>

        <div className="fia-status-row">
          <div className="fia-badge">
            <span className={`fia-dot ${isLive ? 'live' : 'sim'}`}/>
            {isLive ? '🟢 Live data' : '⚪ Simulated'} ·{' '}
            <strong>{totalCount}</strong>&nbsp;events
          </div>
          <div className="fia-legend">
            {Object.entries(TYPE_CFG).slice(0,4).map(([type, cfg]) => (
              <div className="fia-legend-item" key={type}>
                <span className="fia-legend-dot" style={{ background: cfg.ring }}/>
                <span>{cfg.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="fia-globe-wrap" ref={wrapRef}>
          <div className="fia-starfield">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              {Array.from({length:80},(_,i)=>{
                const x=Math.random()*100,y=Math.random()*100,r=Math.random()*1.2+0.3,op=Math.random()*0.5+0.1;
                return <circle key={i} cx={`${x}%`} cy={`${y}%`} r={r} fill="white" opacity={op}/>;
              })}
            </svg>
          </div>
          <canvas ref={canvasRef} className="fia-globe-canvas" style={{ position:'relative', zIndex:2 }}/>
          <div className="fia-globe-overlay">
            {popups.map(ev => (
              <PopupCard key={ev.id} ev={ev} screenPos={screenPositions[ev.id]} onExpire={removePopup}/>
            ))}
          </div>
          <div className="fia-hint">Drag to rotate</div>
        </div>

        <div className="fia-ticker">
          <span className="fia-ticker-label">{isLive ? '🔴 Live' : 'Recent'}</span>
          <div className="fia-ticker-items">
            {tickerItems.map((item, i) => (
              <span key={i} className="fia-ticker-item">
                <span>●</span> {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}